/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useEffect, useId, useRef, useState } from 'react';

import {
  classifyEntry,
  collapseConsecutiveSameKind,
  combineProjectReassignments,
} from '@/Client/Lib';
import { useProjectActivityLog, useScanEditingContextSlice } from '@/Client/State';
import {
  FOCUSED_MODULE_ACTION_STYLE,
  FOCUSED_MODULE_EMPTY_STYLE,
  FOCUSED_MODULE_HEADER_ACTIONS_STYLE,
  FOCUSED_MODULE_HEADER_STYLE,
  FOCUSED_MODULE_SECTION_STYLE,
  FOCUSED_MODULE_TITLE_STYLE,
} from '../FocusedScopeEditor/focusedModuleStyles';
import type { FocusPanelScopeLevel } from '../FocusPanel/scanFocusPanelTypes';
import { FocusedActivityFeedEntry } from './FocusedActivityFeedEntry';

const LIST_STYLE = css`
  label: FOCUSED_ACTIVITY_LIST_STYLE;
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const LOAD_MORE_SENTINEL_STYLE = css`
  label: FOCUSED_ACTIVITY_SENTINEL_STYLE;
  width: 100%;
  height: 1px;
`;

/**
 * How many entries the feed shows before it needs asking. Past this the reader is
 * scrolling a wall of history rather than catching up on what just happened, so
 * the rest waits behind "Show all".
 */
export const FOCUSED_ACTIVITY_COLLAPSED_LIMIT = 8;

/**
 * What the feed calls itself, and what it says when it has nothing — both of which
 * are simply a matter of which scope it is sitting in.
 */
const ACTIVITY_SCOPE_COPY: Record<FocusPanelScopeLevel, { title: string; emptyLabel: string }> = {
  scope: { title: 'Scope activity', emptyLabel: 'No activity yet.' },
  group: { title: 'Group activity', emptyLabel: 'No activity yet in this group.' },
  task: { title: 'Task activity', emptyLabel: 'No activity yet for this task.' },
};

export type FocusedActivityFeedProps = {
  /**
   * Which scope's activity this is. Everything else the feed needs follows from it —
   * what it is called, what it says when empty, and whether the activity log can
   * answer for it at all.
   */
  scopeLevel: FocusPanelScopeLevel;
};

export const FocusedActivityFeed = ({ scopeLevel }: FocusedActivityFeedProps) => {
  const titleId = useId();
  const [showAll, setShowAll] = useState(false);
  const { project } = useScanEditingContextSlice(['project']);
  const { title, emptyLabel } = ACTIVITY_SCOPE_COPY[scopeLevel];
  /**
   * Only all-scope has a log to read. The log records events against a project and a
   * scan, with no group or task column, so a group's or a task's feed has no rows it
   * could draw and renders its empty state — holding the slot until the group- and
   * task-level logs exist.
   */
  const projectId = scopeLevel === 'scope' ? project?.id : undefined;
  const { entries, isLoading, hasMore, loadMore, isLoadingMore } = useProjectActivityLog(projectId, { limit: 20 });
  const loadMoreSentinelRef = useRef<HTMLDivElement | null>(null);
  const hasIntersectionObserver = typeof IntersectionObserver !== 'undefined';

  const classifiedEntries = entries.map(classifyEntry).filter((entry): entry is NonNullable<typeof entry> => !!entry);
  const groupedEntries = combineProjectReassignments(classifiedEntries);
  const collapsedEntries = collapseConsecutiveSameKind(groupedEntries);
  const visibleEntries = showAll ? collapsedEntries : collapsedEntries.slice(0, FOCUSED_ACTIVITY_COLLAPSED_LIMIT);
  const canShowAll = collapsedEntries.length > FOCUSED_ACTIVITY_COLLAPSED_LIMIT;

  // Only page in more history once the reader has asked to see all of it —
  // while the feed is capped, the entries already loaded overflow it anyway.
  useEffect(() => {
    if (!showAll || !hasIntersectionObserver || !hasMore || isLoadingMore || !loadMoreSentinelRef.current) return;
    const observer = new IntersectionObserver(entriesObserved => {
      if (!entriesObserved.some(entry => entry.isIntersecting)) return;
      void loadMore();
    }, { rootMargin: '200px 0px' });
    observer.observe(loadMoreSentinelRef.current);
    return () => observer.disconnect();
  }, [entries.length, hasIntersectionObserver, hasMore, isLoadingMore, loadMore, showAll]);

  const isEmpty = !isLoading && collapsedEntries.length === 0;

  return (
    <section css={FOCUSED_MODULE_SECTION_STYLE} data-focused-activity-feed aria-labelledby={titleId}>
      <div css={FOCUSED_MODULE_HEADER_STYLE}>
        <h3 id={titleId} css={FOCUSED_MODULE_TITLE_STYLE}>{title}</h3>
        {canShowAll
          ? (
            <div css={FOCUSED_MODULE_HEADER_ACTIONS_STYLE}>
              <button
                type={'button'}
                css={FOCUSED_MODULE_ACTION_STYLE}
                onClick={() => setShowAll(current => !current)}
                data-testid={'focused-activity-show-all'}
              >
                {showAll ? 'Show less' : `Show all ${collapsedEntries.length}`}
              </button>
            </div>
          )
          : null}
      </div>
      {isEmpty ? <div css={FOCUSED_MODULE_EMPTY_STYLE}>{emptyLabel}</div> : (
        <ul css={LIST_STYLE} aria-labelledby={titleId}>
          {visibleEntries.map(classified => <FocusedActivityFeedEntry key={classified.entry.id} classified={classified} />)}
        </ul>
      )}
      {showAll && hasMore && hasIntersectionObserver ? <div ref={loadMoreSentinelRef} css={LOAD_MORE_SENTINEL_STYLE} /> : null}
    </section>
  );
};
